import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  TableSortLabel,
  Collapse,
  IconButton,
  Box,
  Typography,
  LinearProgress,
  MuiLink,
} from 'components/mui';
import { AddIcon, RemoveIcon } from 'components/icons';
import { useNavigate } from 'react-router-dom';
import { ROUTE_CUSTOMERS, ROUTE_CONTACTS } from 'constants/routerPath';
import { useActivityLogList } from 'containers/pages/activityLogManagement/hooks';
import { getComparator, stableSort } from 'libs/sortList';
import { ActivityLog, ActivityLogFormData } from 'containers/pages/activityLogManagement/types';
import ActivityLogForm from './ActivityLogForm';

const INITIAL_VISIBLE_COUNT = 50;
const LOAD_MORE_COUNT = 50;
const SCROLL_THRESHOLD = 100;

interface ActivityLogListProps {
  activityLogs: ActivityLog[];
  onSave: (formData: ActivityLogFormData, isEdit: boolean) => Promise<void>;
}

function ActivityLogList({ activityLogs, onSave }: ActivityLogListProps) {
  const { state, handler } = useActivityLogList();
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleNavigate = useCallback((e: React.MouseEvent, targetPath: string) => {
    e.stopPropagation();
    navigate(targetPath);
  }, [navigate]);

  const sortedLogs = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => stableSort(activityLogs, getComparator(state.order, state.orderBy) as any) as ActivityLog[],
    [activityLogs, state.order, state.orderBy],
  );

  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }, [sortedLogs]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    let scrollContainer: HTMLElement | null = el.parentElement;
    while (scrollContainer) {
      const { overflow, overflowY } = getComputedStyle(scrollContainer);
      if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') break;
      scrollContainer = scrollContainer.parentElement;
    }
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer!;
      const nearBottom = scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD;
      if (nearBottom && !isLoadingMore && visibleCount < sortedLogs.length) {
        setIsLoadingMore(true);
        setTimeout(() => {
          setVisibleCount((prev) => Math.min(prev + LOAD_MORE_COUNT, sortedLogs.length));
          setIsLoadingMore(false);
        }, 100);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer!.removeEventListener('scroll', handleScroll);
  }, [isLoadingMore, visibleCount, sortedLogs.length]);

  const visibleRows = useMemo(
    () => sortedLogs.slice(0, visibleCount),
    [sortedLogs, visibleCount],
  );

  const totalCols = state.headTableCells.length;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <Paper ref={listRef} elevation={1}>
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" color="text.secondary">
          Showing {visibleRows.length} of {sortedLogs.length} activity logs
        </Typography>
      </Box>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {state.headTableCells.map((cell) => {
                const isActive = state.orderBy === cell.property;
                const direction = isActive ? state.order : 'asc';
                return (
                  <TableCell key={cell.id} sortDirection={isActive ? state.order : false}
                    sx={cell.width ? { width: cell.width } : undefined}>
                    {cell.property ? (
                      <TableSortLabel active={isActive} direction={direction} onClick={handler.onSort(cell.property)}>
                        {cell.label}
                      </TableSortLabel>
                    ) : null}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={totalCols} align="center" sx={{ py: 6 }}>
                  <Typography sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                    No activity logs found
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {visibleRows.map((log) => {
              const isOpen = state.selectedIds.includes(log.Id);
              return (
                <React.Fragment key={log.Id}>
                  <TableRow hover onClick={() => handler.onToggleExpand(log.Id)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <IconButton aria-label={isOpen ? 'Collapse' : 'Expand'} aria-expanded={isOpen} size="small">
                        {isOpen ? <RemoveIcon /> : <AddIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{log.Title}</TableCell>
                    <TableCell>
                      {log.CustomerId ? (
                        <MuiLink
                          component="button"
                          variant="body2"
                          onClick={(e: React.MouseEvent) => handleNavigate(e, `${ROUTE_CUSTOMERS}/${log.CustomerId}`)}
                          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {log.CustomerName}
                        </MuiLink>
                      ) : (
                        log.CustomerName
                      )}
                    </TableCell>
                    <TableCell>
                      {log.ConsultantContactId ? (
                        <MuiLink
                          component="button"
                          variant="body2"
                          onClick={(e: React.MouseEvent) => handleNavigate(e, `${ROUTE_CONTACTS}/${log.ConsultantContactId}`)}
                          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {log.ConsultantName}
                        </MuiLink>
                      ) : (
                        log.ConsultantName
                      )}
                    </TableCell>
                    <TableCell>{log.StatusTitle}</TableCell>
                    <TableCell>{formatDate(log.CreatedOn)}</TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={{ p: 0, overflowX: 'hidden', maxWidth: '100%' }} colSpan={totalCols}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2 }}>
                          <ActivityLogForm mode="edit" activityLog={log} onSave={onSave} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}

            {isLoadingMore && (
              <TableRow>
                <TableCell colSpan={totalCols}>
                  <Box sx={{ width: '100%', py: 1 }}><LinearProgress /></Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ p: 1, textAlign: 'center' }}>
        {!isLoadingMore && visibleRows.length >= sortedLogs.length && sortedLogs.length > 0 && (
          <Typography variant="caption" color="text.secondary">End of list</Typography>
        )}
      </Box>
    </Paper>
  );
}

export default ActivityLogList;
